import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  CalculatorIcon, 
  ChartBarIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

interface CalculatorInputs {
  teamSize: number;
  averageSalary: number;
  productivityGain: number;
  implementationTime: number;
  aiAssistants: number;
}

const ROICalculator: React.FC = () => {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    teamSize: 20,
    averageSalary: 25000,
    productivityGain: 25,
    implementationTime: 3,
    aiAssistants: 3
  });



  // Calculate ROI based on inputs
  const calculateROI = () => {
    const baseInfrastructure = 29999;
    const assistantPrice = 6000;
    const assistantCount = inputs.aiAssistants;
    const integrationCost = 12000;
    const maintenanceRate = 0.20;

    // Year 1
    const year1Setup = baseInfrastructure + (assistantCount * assistantPrice) + integrationCost;
    const year1Maintenance = year1Setup * maintenanceRate;
    const year1Total = year1Setup + year1Maintenance;
    
    // Productivity value calculation
    const monthlyProductivityValue = (inputs.teamSize * inputs.averageSalary * (inputs.productivityGain / 100)) / 12;
    const year1ProductivityValue = monthlyProductivityValue * (12 - inputs.implementationTime);
    const year1ROI = year1ProductivityValue - year1Total;
    const year1ROIPercentage = (year1ROI / year1Total) * 100;

    // Year 2
    const year2Maintenance = year1Setup * maintenanceRate;
    const year2ProductivityValue = monthlyProductivityValue * 12;
    const year2ROI = year2ProductivityValue - year2Maintenance;
    const year2ROIPercentage = (year2ROI / year2Maintenance) * 100;

    // Year 3
    const year3Maintenance = year1Setup * maintenanceRate;
    const year3ProductivityValue = monthlyProductivityValue * 12;
    const year3ROI = year3ProductivityValue - year3Maintenance;
    const year3ROIPercentage = (year3ROI / year3Maintenance) * 100;

    // Calculate total investment and benefit for 24 months
    const totalInvestment24Months = year1Total + year2Maintenance;
    const totalBenefit24Months = year1ProductivityValue + year2ProductivityValue;
    const totalROI24Months = totalBenefit24Months - totalInvestment24Months;
    const totalROIPercentage24Months = (totalROI24Months / totalInvestment24Months) * 100;

    // Calculate total investment and benefit for 36 months
    const totalInvestment36Months = year1Total + year2Maintenance + year3Maintenance;
    const totalBenefit36Months = year1ProductivityValue + year2ProductivityValue + year3ProductivityValue;
    const totalROI36Months = totalBenefit36Months - totalInvestment36Months;
    const totalROIPercentage36Months = (totalROI36Months / totalInvestment36Months) * 100;

    return {
      year1: { setup: year1Setup, maintenance: year1Maintenance, total: year1Total, productivity: year1ProductivityValue, roi: year1ROI, percentage: year1ROIPercentage },
      year2: { setup: 0, maintenance: year2Maintenance, total: year2Maintenance, productivity: year2ProductivityValue, roi: year2ROI, percentage: year2ROIPercentage },
      year3: { setup: 0, maintenance: year3Maintenance, total: year3Maintenance, productivity: year3ProductivityValue, roi: year3ROI, percentage: year3ROIPercentage },
      total24Months: { investment: totalInvestment24Months, benefit: totalBenefit24Months, roi: totalROI24Months, percentage: totalROIPercentage24Months },
      total36Months: { investment: totalInvestment36Months, benefit: totalBenefit36Months, roi: totalROI36Months, percentage: totalROIPercentage36Months }
    };
  };

  const roiData = calculateROI();

  const handleInputChange = (field: keyof CalculatorInputs, value: number) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-3xl md:text-4xl font-bold text-gray-900 mb-4"
          >
            Calculate Your ROI
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Estimate the productivity gains and cost savings from AI-powered automation.
          </motion.p>
        </div>

        <motion.div 
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="bg-white rounded-2xl shadow-xl overflow-hidden"
        >
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Calculator Inputs */}
            <div className="p-8 bg-gray-50">
              <div className="flex items-center gap-3 mb-6">
                <CalculatorIcon className="w-6 h-6 text-primary-800" />
                <h3 className="text-xl font-bold text-gray-900">Your Business Parameters</h3>
              </div>

              <div className="space-y-6">
                {/* Team Size */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Team Size: {inputs.teamSize} people
                  </label>
                  <input
                    type="range"
                    min="5"
                    max="50"
                    value={inputs.teamSize}
                    onChange={(e) => handleInputChange('teamSize', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>5</span>
                    <span>50</span>
                  </div>
                </div>

                {/* Average Salary */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Average Salary of Knowledge Workers: ${inputs.averageSalary.toLocaleString()}/year
                  </label>
                  <input
                    type="range"
                    min="15000"
                    max="150000"
                    step="5000"
                    value={inputs.averageSalary}
                    onChange={(e) => handleInputChange('averageSalary', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>$15K</span>
                    <span>$150K</span>
                  </div>
                </div>

                {/* Productivity Gain */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Expected Productivity Gain: {inputs.productivityGain}%
                  </label>
                  <input
                    type="range"
                    min="10"
                    max="50"
                    value={inputs.productivityGain}
                    onChange={(e) => handleInputChange('productivityGain', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>10%</span>
                    <span>50%</span>
                  </div>
                </div>

                {/* Implementation Time */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Implementation Time: {inputs.implementationTime} months
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="6"
                    value={inputs.implementationTime}
                    onChange={(e) => handleInputChange('implementationTime', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1 month</span>
                    <span>6 months</span>
                  </div>
                </div>

                {/* AI Assistants */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Number of AI Assistants: {inputs.aiAssistants}
                  </label>
                  <input
                    type="range"
                    min="1"
                    max="10"
                    value={inputs.aiAssistants}
                    onChange={(e) => handleInputChange('aiAssistants', parseInt(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                  />
                  <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>1</span>
                    <span>10</span>
                  </div>
                  <div className="text-xs text-gray-500 mt-1">
                    Each assistant costs $6,000 and can serve multiple team members
                  </div>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <ChartBarIcon className="w-6 h-6 text-primary-800" />
                <h3 className="text-xl font-bold text-gray-900">ROI Results</h3>
              </div>

              {/* QuickReach Results - All Years */}
              <div className="bg-gradient-to-br from-primary-800 to-secondary-800 text-white p-6 rounded-xl mb-6">
                <h4 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <CheckIcon className="w-5 h-5" />
                  QuickReach Ownership
                </h4>
                
                <div className="space-y-4">
                  {/* Year 1 */}
                  <div className="border-b border-white/20 pb-3">
                    <h5 className="font-semibold mb-2">Year 1</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Setup Investment:</span>
                        <span className="font-bold">${roiData.year1.setup.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Annual Maintenance:</span>
                        <span className="font-bold">${roiData.year1.maintenance.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Productivity Value:</span>
                        <span className="font-bold text-green-300">+${roiData.year1.productivity.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-lg border-t border-white/20 pt-2">
                        <span>Net ROI:</span>
                        <span className={`font-bold ${roiData.year1.roi >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                          {roiData.year1.roi >= 0 ? '+' : ''}${roiData.year1.roi.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs opacity-90">
                        <span>ROI Percentage:</span>
                        <span className={`font-bold ${roiData.year1.percentage >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                          {roiData.year1.percentage >= 0 ? '+' : ''}{roiData.year1.percentage.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Year 2 */}
                  <div className="border-b border-white/20 pb-3">
                    <h5 className="font-semibold mb-2">Year 2</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Annual Maintenance:</span>
                        <span className="font-bold">${roiData.year2.maintenance.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Productivity Value:</span>
                        <span className="font-bold text-green-300">+${roiData.year2.productivity.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-lg border-t border-white/20 pt-2">
                        <span>Net ROI:</span>
                        <span className={`font-bold ${roiData.year2.roi >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                          {roiData.year2.roi >= 0 ? '+' : ''}${roiData.year2.roi.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs opacity-90">
                        <span>ROI Percentage:</span>
                        <span className={`font-bold ${roiData.year2.percentage >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                          {roiData.year2.percentage >= 0 ? '+' : ''}{roiData.year2.percentage.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Year 3 */}
                  <div>
                    <h5 className="font-semibold mb-2">Year 3</h5>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span>Annual Maintenance:</span>
                        <span className="font-bold">${roiData.year3.maintenance.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Productivity Value:</span>
                        <span className="font-bold text-green-300">+${roiData.year3.productivity.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between text-lg border-t border-white/20 pt-2">
                        <span>Net ROI:</span>
                        <span className={`font-bold ${roiData.year3.roi >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                          {roiData.year3.roi >= 0 ? '+' : ''}${roiData.year3.roi.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between text-xs opacity-90">
                        <span>ROI Percentage:</span>
                        <span className={`font-bold ${roiData.year3.percentage >= 0 ? 'text-green-300' : 'text-red-300'}`}>
                          {roiData.year3.percentage >= 0 ? '+' : ''}{roiData.year3.percentage.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comparison Scenarios */}
              {/* Removed AI Rental and Custom Development comparison boxes as requested */}
            </div>
          </div>
        </motion.div>

        {/* 24-Month ROI Summary */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          viewport={{ once: true }}
          className="mt-8 bg-gradient-to-r from-primary-800 to-secondary-800 text-white p-6 rounded-xl"
        >
          <div className="text-center">
            <h3 className="text-xl font-bold mb-4">24-Month ROI Summary</h3>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white/10 p-4 rounded-lg">
                <div className="text-2xl font-bold text-green-300">
                  +${roiData.total24Months.roi.toLocaleString()}
                </div>
                <div className="text-sm opacity-90">Total Net ROI (24 months)</div>
              </div>
              <div className="bg-white/10 p-4 rounded-lg">
                <div className="text-2xl font-bold text-blue-300">
                  {roiData.total24Months.percentage.toFixed(0)}%
                </div>
                <div className="text-sm opacity-90">ROI on Total Investment</div>
              </div>
              <div className="bg-white/10 p-4 rounded-lg">
                <div className="text-2xl font-bold text-yellow-300">
                  ${roiData.total24Months.investment.toLocaleString()}
                </div>
                <div className="text-sm opacity-90">Total Investment</div>
              </div>
              <div className="bg-white/10 p-4 rounded-lg">
                <div className="text-2xl font-bold text-purple-300">
                  ${roiData.total24Months.benefit.toLocaleString()}
                </div>
                <div className="text-sm opacity-90">Total Productivity Value</div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Calculator Footer */}
        <motion.div 
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          viewport={{ once: true }}
          className="mt-6 bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg"
        >
          <p className="text-sm text-blue-800">
            <strong>Key Assumptions:</strong> Productivity gains based on industry averages. Custom development includes team costs, infrastructure, and 18-month timeline. AI rental assumes limited productivity due to copy-paste workflows and no business context.
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default ROICalculator; 